/**
 * iOS-safe navigation hooks.
 *
 * This module wraps React Router hooks to ensure URL parameters are preserved
 * with iOS-compatible encoding throughout the navigation flow.
 *
 * CRITICAL: This hook uses UrlManipulation.modifyUrlParams instead of URLSearchParams
 * to avoid encoding issues with iOS apps:
 * - URLSearchParams.toString() encodes spaces as '+' (form-urlencoded)
 * - iOS apps do not decode '+' in URLs
 * - UrlManipulation preserves original '%20' encoding
 *
 * IMPORTANT: Always import navigation hooks from this file, NOT from react-router-dom directly.
 * This ensures the redirect chain to iOS apps maintains proper URL encoding.
 */
import {
  useNavigate as useReactRouterNavigate,
  useSearchParams as useReactRouterSearchParams,
} from 'react-router-dom';
import { useCallback } from 'react';
import { UrlManipulation } from '@workivate/tho-web-shared';

// Re-export these hooks as-is for consumer use
// IMPORTANT: useSearchParams is SAFE to use for READING params (e.g., searchParams.get('client_id'))
// The encoding issue only occurs when WRITING with searchParams.toString(), which our custom
// useNavigate prevents. Consumers should use useSearchParams for reading and useNavigate for navigation.
export { useLocation, useSearchParams } from 'react-router-dom';

/**
 * iOS-safe navigation hook that automatically preserves URL parameters.
 *
 * Uses UrlManipulation to ensure params are not double-encoded and spaces
 * remain as '%20' (not '+'), maintaining compatibility with iOS app redirects.
 *
 * @example
 * const navigate = useNavigate();
 * navigate(routes['/login']); // Params automatically preserved with iOS-safe encoding
 * navigate(routes['/error'], { replace: true }); // With options
 * navigate({ pathname: routes['/login'], search: '?lang=fr_CA' }); // Object-style for param modification
 */
export const useNavigate = () => {
  const navigate = useReactRouterNavigate();
  const [searchParams] = useReactRouterSearchParams();

  return useCallback(
    (
      to: string | { pathname: string; search?: string },
      options?: { replace?: boolean; state?: any }
    ) => {
      // Handle object-style navigation (used by locale.tsx for param modification)
      if (typeof to === 'object') {
        navigate(to, options);
        return;
      }

      // Parse param names already present in the target URL using raw string operations
      // (avoids URLSearchParams/URL object which would re-encode values)
      // Params explicitly set in the target URL take precedence over current searchParams
      const targetSearch = to.includes('?')
        ? (to.split('?')[1] ?? '').split('#')[0]
        : '';
      const targetParamNames = new Set(
        targetSearch ? targetSearch.split('&').map((p) => p.split('=')[0]) : []
      );

      // Convert searchParams to operations array, excluding params already in the target URL
      // IMPORTANT: searchParams.entries() is SAFE to use for READING params
      // It returns DECODED values which we then re-encode using UrlManipulation
      // This avoids sending parameters to the iOS app on final redirect that it can't correctly decode
      const operations = Array.from(searchParams.entries())
        .filter(([name]) => !targetParamNames.has(name))
        .map(([name, value]) => ({ name, value }));

      // Use UrlManipulation to preserve encoding (iOS-safe)
      // This avoids URLSearchParams.toString() which would convert %20 to +
      const finalPath =
        operations.length > 0
          ? UrlManipulation.modifyUrlParams<string>({
              url: to,
              operations,
            })
          : to;

      navigate(finalPath, options);
    },
    [navigate, searchParams]
  );
};
